import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, CornerDownLeft } from "lucide-react";
import useIsHeld from "../hooks/useIsHeld";

// Somebody held on an agency's roster is not offered other work or a public
// availability post, wherever this navigator appears.
const HELD_HIDDEN = new Set(["/postings", "/browse", "/browse?show=job", "/browse?show=person", "/dashboard/contractor/postings"]);

const SANS = "'Instrument Sans', sans-serif";

/* ------------------------------------------------------------------ */
/*  Navigator — type to jump to any destination or common action.      */
/*  Shared across the hub and the dashboards. `actions` is a list of    */
/*  { label, href, kw } where kw is a space-joined keyword string.      */
/* ------------------------------------------------------------------ */
export default function ConciergeBar({ actions: allActions = [], placeholder = "How can we help?" }) {
  const navigate = useNavigate();
  const held = useIsHeld();
  const actions = useMemo(
    () => (held ? allActions.filter((a) => !HELD_HIDDEN.has(a.href)) : allActions),
    [held, allActions]
  );
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  const matches = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return actions
      .map((a) => ({ a, hit: `${a.label} ${a.kw || ""}`.toLowerCase().includes(term), inLabel: a.label.toLowerCase().includes(term) }))
      .filter((x) => x.hit)
      .sort((x, y) => Number(y.inLabel) - Number(x.inLabel))
      .slice(0, 6)
      .map((x) => x.a);
  }, [q, actions]);

  useEffect(() => { setActive(0); }, [q]);
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const go = (a) => { if (a) { setOpen(false); setQ(""); navigate(a.href); } };
  const onKey = (e) => {
    if (!open || !matches.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % matches.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i - 1 + matches.length) % matches.length); }
    else if (e.key === "Enter") { e.preventDefault(); go(matches[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={boxRef} style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <style>{`.cbar-item:hover,.cbar-item[data-active="true"]{background:var(--brand-tint);}`}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 10, background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 999, boxShadow: "var(--shadow-sm)", padding: "10.5px 18px" }}>
        <Search size={19} strokeWidth={1.7} color="var(--text-muted)" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey}
          placeholder={placeholder} aria-label="Navigate Sift"
          style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: "0.9375rem", color: "var(--ink)", fontFamily: SANS, minWidth: 0, minHeight: 24 }} />
        {q && <CornerDownLeft size={16} color="var(--text-muted)" aria-hidden="true" />}
      </div>
      {open && q.trim() && (
        <div style={{ position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0, zIndex: 40, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 16, boxShadow: "var(--shadow-lg)", overflow: "hidden" }}>
          {matches.length === 0 ? (
            <div style={{ padding: "16px 18px", fontSize: "0.875rem", color: "var(--text-muted)" }}>No match. Try a page name or an action.</div>
          ) : matches.map((a, i) => (
            <button key={a.href + a.label} type="button" className="cbar-item" data-active={i === active} onMouseEnter={() => setActive(i)} onClick={() => go(a)}
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, width: "100%", textAlign: "left", border: "none", background: "transparent", cursor: "pointer", padding: "12px 18px", fontFamily: SANS }}>
              <span style={{ fontSize: "0.90625rem", color: "var(--ink)", fontWeight: 500 }}>{a.label}</span>
              <ArrowRight size={15} color="var(--brand)" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
